/**
 * Rider Management API Hooks
 * React hooks for managing rider data
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import * as React from 'react';
import { adminApiClient } from './adminApiClient';
import { RiderDTO } from './adminTypes';
import { ApiResponse, Pageable, PagedData } from './types';

/**
 * Hook to fetch a single rider by ID
 * @param id - Rider ID
 * @param options - Hook options
 */
export function useRider(
  id: number,
  options: {
    enabled?: boolean;
    onSuccess?: (data: RiderDTO) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  // Use useCallback to memoize the query function and prevent infinite re-renders
  const queryFn = useCallback(async () => {
    const response = await adminApiClient.get<ApiResponse<RiderDTO>>(`/riders/${id}`);

    // Handle Response wrapper - check if response has data property
    if (response && typeof response === 'object' && 'data' in response) {
      return response.data;
    }

    // If response is directly the rider object
    return response as RiderDTO;
  }, [id]);

  const query = useQuery<RiderDTO>({
    queryKey: ['rider', id],
    queryFn,
    enabled: options.enabled !== false && !!id,
  });

  // Handle callbacks using useEffect pattern (React Query v5)
  React.useEffect(() => {
    if (query.isSuccess && query.data && options.onSuccess) {
      options.onSuccess(query.data);
    }
  }, [query.isSuccess, query.data, options.onSuccess]);

  React.useEffect(() => {
    if (query.isError && query.error && options.onError) {
      options.onError(query.error);
    }
  }, [query.isError, query.error, options.onError]);

  return {
    ...query,
    data: query.data,
    loading: query.isLoading || query.isFetching,
    error: query.error,
  };
}

/**
 * Hook to fetch paginated list of riders
 * @param params - Query parameters (page, size, sort, search)
 * @param options - Hook options
 */
export function useRiders(
  params: {
    page?: number;
    size?: number;
    sort?: string[];
    search?: string;
  } = {},
  options: {
    enabled?: boolean;
    onSuccess?: (data: PagedData<RiderDTO>) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  // Use useCallback to memoize the query function and prevent infinite re-renders
  const queryFn = useCallback(async () => {
    const queryParams: Record<string, any> = {};

    if (params.page !== undefined) {
      queryParams.page = params.page;
    }
    if (params.size !== undefined) {
      queryParams.size = params.size;
    }
    if (params.sort && params.sort.length > 0) {
      queryParams.sort = params.sort;
    }
    if (params.search) {
      queryParams.search = params.search;
    }

    const response = await adminApiClient.get<ApiResponse<PagedData<RiderDTO>>>(
      '/riders/list',
      queryParams
    );

    // Handle Response wrapper
    if (response.data) {
      return response.data;
    }

    // If data is directly in response (array)
    if (Array.isArray(response)) {
      return {
        content: response,
        page: {
          size: response.length,
          number: 0,
          totalElements: response.length,
          totalPages: 1,
        },
        first: true,
        last: true,
        empty: response.length === 0,
      };
    }

    return response as any;
  }, [params.page, params.size, params.sort, params.search]);

  const query = useQuery<PagedData<RiderDTO>>({
    queryKey: ['riders', params],
    queryFn,
    enabled: options.enabled !== false,
  });

  // Handle callbacks using useEffect pattern (React Query v5)
  React.useEffect(() => {
    if (query.isSuccess && query.data && options.onSuccess) {
      options.onSuccess(query.data);
    }
  }, [query.isSuccess, query.data, options.onSuccess]);

  React.useEffect(() => {
    if (query.isError && query.error && options.onError) {
      options.onError(query.error);
    }
  }, [query.isError, query.error, options.onError]);

  return query;
}

/**
 * Hook to create a new rider
 * @param options - Hook options
 */
export function useCreateRider(
  options: {
    onSuccess?: (data: ApiResponse<RiderDTO>, variables: RiderDTO) => void;
    onError?: (error: Error, variables: RiderDTO) => void;
  } = {}
) {
  const queryClient = useQueryClient();
  
  return useMutation<ApiResponse<RiderDTO>, Error, RiderDTO>({
    mutationFn: async (riderData: RiderDTO) => {
      const response = await adminApiClient.post<ApiResponse<RiderDTO>>('/riders', riderData);
      return response;
    },
    onSuccess: (data, variables) => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ['riders'] });
      
      if (options.onSuccess) {
        options.onSuccess(data, variables);
      }
    },
    onError: options.onError,
  });
}

/**
 * Hook to update an existing rider
 * @param options - Hook options
 */
export function useUpdateRider(
  options: {
    onSuccess?: (
      data: ApiResponse<RiderDTO>,
      variables: { id: number; data: RiderDTO }
    ) => void;
    onError?: (error: Error, variables: { id: number; data: RiderDTO }) => void;
  } = {}
) {
  const queryClient = useQueryClient();
  
  return useMutation<ApiResponse<RiderDTO>, Error, { id: number; data: RiderDTO }>({
    mutationFn: async ({ id, data }: { id: number; data: RiderDTO }) => {
      const response = await adminApiClient.put<ApiResponse<RiderDTO>>(`/riders/${id}`, data);
      return response;
    },
    onSuccess: (data, variables) => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ['riders'] });
      queryClient.invalidateQueries({ queryKey: ['rider', variables.id] });
      
      if (options.onSuccess) {
        options.onSuccess(data, variables);
      }
    },
    onError: options.onError,
  });
}

/**
 * Hook to delete a rider
 * @param options - Hook options
 */
export function useDeleteRider(
  options: {
    onSuccess?: (data: ApiResponse<void>, variables: DeleteRiderVariables) => void;
    onError?: (error: Error, variables: DeleteRiderVariables) => void;
  } = {}
) {
  const queryClient = useQueryClient();
  
  return useMutation<ApiResponse<void>, Error, DeleteRiderVariables>({
    mutationFn: async (variables: DeleteRiderVariables) => {
      // First delete the rider
      const response = await adminApiClient.delete<ApiResponse<void>>(`/riders/${variables.riderId}`);

      // Then find and delete the corresponding user if email is provided
      if (variables.email) {
        try {
          const userResponse = await adminApiClient.get<any>(`/api/admin/user-by-email?email=${encodeURIComponent(variables.email)}`);
          if (userResponse.data?.id) {
            await adminApiClient.delete<ApiResponse<void>>(`/api/admin/${userResponse.data.id}`);
          }
        } catch (userError) {
          // Log the error but don't fail the entire operation
          console.warn('Failed to delete corresponding user:', userError);
        }
      }

      return response;
    },
    onSuccess: (data, variables) => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ['riders'] });
      queryClient.invalidateQueries({ queryKey: ['rider', variables.riderId] });
      
      if (options.onSuccess) {
        options.onSuccess(data, variables);
      }
    },
    onError: options.onError,
  });
}

/**
 * Variables for delete rider operation
 */
export interface DeleteRiderVariables {
  riderId: number;
  email?: string;
  phoneNumber?: string;
}

/**
 * Hook to toggle rider active status
 * @param options - Hook options
 */
export function useToggleRiderActiveStatus(
  options: {
    onSuccess?: (data: ApiResponse<void>, variables: number) => void;
    onError?: (error: Error, variables: number) => void;
  } = {}
) {
  const queryClient = useQueryClient();
  
  return useMutation<ApiResponse<void>, Error, number>({
    mutationFn: async (id: number) => {
      const response = await adminApiClient.get<ApiResponse<void>>(`/riders/toggle-active-status/${id}`);
      return response;
    },
    onSuccess: (data, variables) => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ['riders'] });
      queryClient.invalidateQueries({ queryKey: ['rider', variables] });
      
      if (options.onSuccess) {
        options.onSuccess(data, variables);
      }
    },
    onError: options.onError,
  });
}
